import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { PartyPopper, CheckCircle, Star, Users, Award, Clock, Globe, BookOpen } from "lucide-react";
import BlogPosts from "../../components/BlogSection";
import ContactUsForm from "../ContactForm";
import Updates from "../Home/Updates";
import GoogleReviews from "../GoogleReviews";

const EnglishProficiency = () => {
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  const navigate = useNavigate();

  // Exam information with updated redirect paths
  const examInfo = {
    IELTS: {
      fullName: "International English Language Testing System",
      description: "A standardized test that measures the English language proficiency of non-native English speakers.",
      keyFeatures: [
        "Four skills tested: Listening, Reading, Writing, Speaking",
        "Academic & General Training formats available",
        "Results within 13 calendar days",
        "Valid for 2 years"
      ],
      averageScore: "Band score of 6.0-7.0 typically required",
      page: "../english-proficiency/ielts",
      icon: "🇬🇧",
      color: "from-blue-500 to-blue-700"
    },
    TOEFL: {
      fullName: "Test of English as a Foreign Language",
      description: "An American English proficiency test accepted by 11,000+ universities in 150+ countries.",
      keyFeatures: [
        "Four skills with integrated tasks",
        "Internet-based test (iBT) format",
        "Results within 10 days",
        "Valid for 2 years"
      ],
      averageScore: "Score of 80-100 (out of 120) typically required",
      page: "../english-proficiency/tofel",
      icon: "🇺🇸",
      color: "from-red-500 to-red-700"
    },
    PTE: {
      fullName: "Pearson Test of English Academic",
      description: "A computer-based English test known for fast results and objective scoring.",
      keyFeatures: [
        "Fully computer-based including speaking",
        "Results within 48 hours",
        "Accepted worldwide at thousands of institutions",
        "Valid for 2 years"
      ],
      averageScore: "Score of 58-65 (out of 90) typically required",
      page: "../english-proficiency/pte",
      icon: "💻",
      color: "from-green-500 to-green-700"
    },
    Duolingo: {
      fullName: "Duolingo English Test",
      description: "A modern, accessible English proficiency test taken online in about 1 hour.",
      keyFeatures: [
        "Online adaptive format",
        "Results within 48 hours",
        "Significantly lower cost",
        "Valid for 2 years"
      ],
      averageScore: "Score of 110-120 (out of 160) typically required",
      page: "../english-proficiency/duolingo",
      icon: "🦉",
      color: "from-emerald-500 to-emerald-700"
    }
  };

  const handleExamClick = (exam) => {
    if (selectedExam === exam) {
      navigate(examInfo[exam].page);
    } else {
      setSelectedExam(exam);
    }
  };

  const stats = [
    { icon: Users, number: "10,000+", label: "Students Trained" },
    { icon: Award, number: "95%", label: "Success Rate" },
    { icon: Globe, number: "25+", label: "Countries" },
    { icon: BookOpen, number: "25+", label: "Years Experience" }
  ];

  const pricingPlans = [
    {
      title: "Common Class",
      price: "₹12,000",
      duration: "3 Months",
      popular: false,
      features: [
        "Daily 1.5 Hours Lecture",
        "2 Problem-Solving Sessions/Week",
        "20 Mock Tests",
        "Study Materials Included",
        "Online Support"
      ],
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "Personalised Coaching",
      price: "₹20,000",
      duration: "3 Months",
      popular: true,
      features: [
        "1:1 Exclusive Coaching",
        "Daily 2 Hours Lecture",
        "50 Mock Tests",
        "Complete Study Abroad Guidance",
        "Free Visa Process Assistance",
        "₹2,000 Flight Voucher"
      ],
      color: "from-purple-500 to-purple-600"
    },
    {
      title: "Duolingo Preparation",
      price: "₹5,000",
      duration: "1 Month",
      popular: false,
      features: [
        "Comprehensive Study Material",
        "Test Strategies & Tips",
        "Mock Test Practice",
        "Quick Preparation Plan",
        "24/7 Support"
      ],
      color: "from-green-500 to-green-600"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      country: "Australia",
      exam: "IELTS",
      score: "Band 8.0",
      rating: 5,
      text: "The personalized coaching helped me achieve my dream score! The instructors were incredibly supportive.",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b812?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Michael Chen",
      country: "Canada",
      exam: "TOEFL",
      score: "115/120",
      rating: 5,
      text: "Excellent preparation materials and mock tests. I felt completely prepared for the actual exam.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
    },
    {
      name: "Priya Sharma",
      country: "UK",
      exam: "PTE",
      score: "85/90",
      rating: 5,
      text: "The computer-based practice sessions were exactly like the real test. Highly recommend!",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
    }
  ];

  const faqs = [
    {
      question: "How long does it take to prepare for English proficiency tests?",
      answer: "Preparation time varies based on your current level and target score. Typically, 2-3 months of consistent study (10-15 hours per week) is recommended for optimal results."
    },
    {
      question: "Which test should I choose: IELTS, TOEFL, PTE, or Duolingo?",
      answer: "It depends on your target institution's requirements. IELTS is preferred for UK/Australia, TOEFL for US institutions, PTE for quick results, and Duolingo for convenience and affordability."
    },
    {
      question: "Do you offer money-back guarantees?",
      answer: "Yes, we offer a partial refund if you don't see improvement in practice test scores after completing 80% of the program and taking all recommended tests."
    },
    {
      question: "What are your success rates?",
      answer: "Our students achieve target scores at a 95% success rate, which is 37% higher than industry average, thanks to our personalized approach and expert instructors."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 flex items-center justify-center gap-2">

        <span className="font-medium text-sm md:text-base">
          🎉 Limited Time Offer: Get 20% OFF on all English Proficiency courses!
        </span>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-primary-cream py-20 sm:py-32">
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1
              className="text-4xl md:text-6xl font-bold text-primary mb-6"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Master English Proficiency
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Unlock Global Opportunities
              </span>
            </motion.h1>
            
            <motion.p
              className="text-xl md:text-2xl text-primary mb-8 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Achieve your target scores in IELTS, TOEFL, PTE, and Duolingo with our expert-led preparation programs
            </motion.p>

            <motion.div
              className="flex flex-wrap justify-center gap-4 mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {/* <button
                onClick={() => setShowDemoPopup(true)}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                📚 Book Free Demo
              </button> */}
              <button onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })} className="border-2 border-white text-primary px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-all duration-300">
              📚 Book Free Demo
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <stat.icon className="w-8 h-8 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-primary">{stat.number}</div>
                  <div className="text-primary">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Exam Selection Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-primary mb-4">
              Choose Your Exam
            </h2>
            <p className="text-xl text-primary max-w-2xl mx-auto">
              Select the English proficiency test that matches your academic goals and destination country
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {Object.keys(examInfo).map((exam, index) => (
              <motion.div
                key={exam}
                className="group cursor-pointer"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => handleExamClick(exam)}
              >
                <div className="bg-white min-h-[425px] rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 group-hover:border-blue-200">
                  <div className={`h-2 bg-gradient-to-r ${examInfo[exam].color}`}></div>
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">{exam}</h3>
                      <span className="text-3xl">{examInfo[exam].icon}</span>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-3">{examInfo[exam].fullName}</p>
                    <p className="text-gray-700 mb-4 text-sm leading-relaxed">{examInfo[exam].description}</p>
                    
                    <div className="space-y-2 mb-4">
                      {examInfo[exam].keyFeatures.slice(0, 2).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="text-sm font-medium text-blue-600 mb-4">
                      {examInfo[exam].averageScore}
                    </div>
                    
                    {/* <button className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300">
                      Learn More
                    </button> */}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Hear from our students who achieved their dream scores
            </p>
          </motion.div>
          <GoogleReviews />
          {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
                
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                
                <div className="flex items-center gap-4">
                  
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.country} • {testimonial.exam}: {testimonial.score}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div> */}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our English proficiency courses
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <button
                  className="w-full text-left p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex justify-between items-center"
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                >
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                  <span className={`text-2xl transition-transform duration-300 ${openFAQ === index ? 'rotate-180' : ''}`}>
                    ↓
                  </span>
                </button>
                
                {openFAQ === index && (
                  <motion.div
                    className="mt-2 p-6 bg-blue-50 rounded-2xl"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Updates />
      
      <ContactUsForm />

      {/* Demo Popup */}
      {showDemoPopup && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={(e) => e.target === e.currentTarget && setShowDemoPopup(false)}
        >
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-lg w-full shadow-2xl"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-white animate-spin" />
              </div>
              
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Coming Soon!</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Our online demo booking system is under development. Please contact us directly to schedule your free demo session.
              </p>
              
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => setShowDemoPopup(false)}
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowDemoPopup(false);
                    // Scroll to contact section
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default EnglishProficiency;